import { useState, useEffect, useMemo, type PropsWithChildren } from "react";
import { useModuleBEM, useUiStyles } from "@b2bportal/core-themes";
import { CoreUiComponents } from "@b2bportal/core-types";
import clsx from "clsx";
import defaultStyles from "./SliderDots.module.scss";

export interface SliderDotsProps {
  goLeft: boolean;
  setGoLeft: (goLeft: boolean) => void;
  goRight: boolean;
  setGoRight: (goRight: boolean) => void;
  numberOfImages: number;
  currentIndex: number;
}

const DEFAULT_DOT_SIZE = 7;
const DEFAULT_SPACING_SIZE = 5;
// dots number must be odd
const DEFAULT_DOTS_NUMBER = 9;

export const SliderDots = ({
  goLeft,
  setGoLeft,
  goRight,
  setGoRight,
  numberOfImages,
  currentIndex,
}: SliderDotsProps) => {
  const COMPONENT_KEY = CoreUiComponents.CarouselSliderDots;
  const styles = useUiStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const [isMoving, setIsMoving] = useState(false);
  useEffect(() => {
    // the isMoving field is used to prevent the moving effect from taking place twice at the same time
    if ((goLeft || goRight) && !isMoving) {
      setIsMoving(true);
      setTimeout(() => {
        goLeft && setGoLeft(false);
        goRight && setGoRight(false);
        setIsMoving(false);
        // the duration of slide animation is set to 0.2s (see styles.scss)
      }, 200);
    }
  }, [goLeft, goRight, isMoving, setGoLeft, setGoRight]);

  return (
    <div className={clsx(block, cn("root"))}>
      <Dots
        goLeft={goLeft}
        goRight={goRight}
        numberOfImages={numberOfImages}
        currentIndex={currentIndex}
      />
    </div>
  );
};

type DotProps = {
  dotWidth: number;
  dotMarginRight: number;
  className: string;
};

const Dot = ({ dotWidth, dotMarginRight, className }: DotProps) => {
  const styles = {
    width: `${dotWidth}px`,
    height: `${dotWidth}px`,
    marginRight: `${dotMarginRight}px`,
  };
  return <div className={className} style={styles} />;
};

type DotsWrapperProps = {
  dotsWrapperWidth: number;
  className: string;
};

const DotsWrapper = ({
  dotsWrapperWidth,
  className,
  children,
}: PropsWithChildren<DotsWrapperProps>) => {
  return (
    <div
      className={className}
      style={{
        width: `${dotsWrapperWidth}px`,
        left: `calc(50% - ${dotsWrapperWidth / 2}px)`,
      }}
    >
      {children}
    </div>
  );
};

type DotsContainerProps = {
  dotsWrapperWidth: number;
  dotWidth: number;
  className: string;
};

const DotsContainer = ({
  dotsWrapperWidth,
  dotWidth,
  className,
  children,
}: PropsWithChildren<DotsContainerProps>) => {
  return (
    <div
      className={className}
      style={{ width: `${dotsWrapperWidth}px`, height: `${2.5 * dotWidth}px` }}
    >
      {children}
    </div>
  );
};

type DotsProps = {
  goLeft: boolean;
  goRight: boolean;
  numberOfImages: number;
  currentIndex: number;
};

function Dots({ goLeft, goRight, numberOfImages, currentIndex }: DotsProps) {
  const COMPONENT_KEY = CoreUiComponents.CarouselSliderDots;
  const styles = useUiStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const dotWidth = DEFAULT_DOT_SIZE;
  const dotMarginRight = DEFAULT_SPACING_SIZE;
  const numberOfDots = Math.min(numberOfImages, DEFAULT_DOTS_NUMBER);

  const middlePosition = Math.ceil(numberOfDots / 2);

  const shouldShowAnimation = useMemo(
    () => numberOfImages > numberOfDots,
    [numberOfDots, numberOfImages]
  );

  const currentIndexIsInInitialSectionOfDots = useMemo(
    () => currentIndex < middlePosition,
    [currentIndex, middlePosition]
  );
  const currentIndexIsInEndSectionOfDots = useMemo(
    () => currentIndex >= numberOfImages - middlePosition,
    [currentIndex, middlePosition, numberOfImages]
  );

  const shouldShouldShowLeftAnimation = useMemo(() => {
    return (
      shouldShowAnimation &&
      currentIndex >= middlePosition - 1 &&
      currentIndex < numberOfImages - middlePosition
    );
  }, [currentIndex, middlePosition, numberOfImages, shouldShowAnimation]);

  const shouldShouldShowRightAnimation = useMemo(() => {
    return (
      shouldShowAnimation &&
      currentIndex >= middlePosition &&
      currentIndex <= numberOfImages - middlePosition
    );
  }, [currentIndex, middlePosition, numberOfImages, shouldShowAnimation]);

  const dots: JSX.Element[] = [];
  for (let i = 0; i < numberOfDots; i++) {
    dots.push(
      <Dot
        key={i + 1}
        className={cn("dot", {
          "left-most": i === 0,
          "right-most": i === numberOfDots - 1,
          focused: currentIndexIsInEndSectionOfDots
            ? i === numberOfDots - (numberOfImages - currentIndex)
            : currentIndexIsInInitialSectionOfDots
              ? i === currentIndex
              : i === middlePosition - 1,
          "focused-left": i === Math.floor(numberOfDots / 2) - 1,
          "focused-right": i === Math.floor(numberOfDots / 2) + 1,
          "go-left": goLeft && shouldShouldShowLeftAnimation,
          "go-right": goRight && shouldShouldShowRightAnimation,
        })}
        dotWidth={dotWidth}
        dotMarginRight={dotMarginRight}
      />
    );
  }

  dots.unshift(
    <Dot
      key={0}
      className={cn("dot", {
        "go-right": goRight,
        hidden: !goLeft || !shouldShouldShowLeftAnimation,
      })}
      dotWidth={dotWidth}
      dotMarginRight={dotMarginRight}
    />
  );
  dots.push(
    <Dot
      key={numberOfDots + 1}
      className={cn("dot", {
        "go-left": goLeft,
        hidden: !goRight || !shouldShouldShowRightAnimation,
      })}
      dotWidth={dotWidth}
      dotMarginRight={0}
    />
  );

  const dotsContainerWidth =
    numberOfDots * dotWidth + (numberOfDots + 1) * dotMarginRight;

  const dotsWrapperWidth =
    (numberOfDots + 2) * dotWidth + (numberOfDots + 1) * dotMarginRight;

  return (
    <DotsContainer
      className={cn("container")}
      dotWidth={dotWidth}
      dotsWrapperWidth={dotsContainerWidth}
    >
      <DotsWrapper
        className={cn("wrapper", {
          "go-left": goLeft && shouldShouldShowLeftAnimation,
          "go-right": goRight && shouldShouldShowRightAnimation,
        })}
        dotsWrapperWidth={dotsWrapperWidth}
      >
        {dots}
      </DotsWrapper>
    </DotsContainer>
  );
}
